import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, CalendarDays, X } from 'lucide-react';
import styles from './ClayDatePicker.module.css';

interface ClayDatePickerProps {
  value: string;          // 'YYYY-MM-DD' or ''
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
}

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parseDate(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function toYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplay(value: string): string {
  const d = parseDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

const POPOVER_WIDTH = 252;

export function ClayDatePicker({ value, onChange, placeholder = 'Pick a date', label }: ClayDatePickerProps) {
  const selected = parseDate(value);
  const today = new Date();

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(selected?.getFullYear() ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected?.getMonth() ?? today.getMonth());
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Calculate popover position from trigger rect
  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    let left = rect.left;
    // Prevent going off the right edge
    if (left + POPOVER_WIDTH > window.innerWidth - 12) {
      left = window.innerWidth - POPOVER_WIDTH - 12;
    }
    setPopoverStyle({
      position: 'fixed',
      top: rect.bottom + gap,
      left,
      width: POPOVER_WIDTH,
    });
  }, []);

  const openPicker = () => {
    calcPosition();
    setOpen(true);
  };

  // Sync view when value changes externally
  useEffect(() => {
    if (selected) {
      setViewYear(selected.getFullYear()); // eslint-disable-line react-hooks/set-state-in-effect
      setViewMonth(selected.getMonth());
    }
  }, [value, selected]);

  // Reposition on scroll / resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => calcPosition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, calcPosition]);

  // Close on outside click — must check both trigger and portaled popover
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const prevMonth = useCallback(() => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  }, [viewMonth]);

  const nextMonth = useCallback(() => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  }, [viewMonth]);

  const selectDay = (day: number) => {
    onChange(toYMD(new Date(viewYear, viewMonth, day)));
    setOpen(false);
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  // Build calendar grid
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDow = getFirstDayOfWeek(viewYear, viewMonth);
  const cells: Array<number | null> = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const todayYMD = toYMD(today);

  const calendar = (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={popoverRef}
          className={styles.popover}
          style={popoverStyle}
          initial={{ opacity: 0, y: -6, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 420, damping: 28 }}
        >
          {/* Month navigation */}
          <div className={styles.nav}>
            <motion.button
              type="button"
              className={styles['nav-btn']}
              onClick={prevMonth}
              whileTap={{ scale: 0.88 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <ChevronLeft style={{ width: 15, height: 15 }} />
            </motion.button>

            <span className={styles['nav-title']}>
              {MONTHS[viewMonth]} {viewYear}
            </span>

            <motion.button
              type="button"
              className={styles['nav-btn']}
              onClick={nextMonth}
              whileTap={{ scale: 0.88 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <ChevronRight style={{ width: 15, height: 15 }} />
            </motion.button>
          </div>

          {/* Day-of-week headers */}
          <div className={styles['dow-grid']}>
            {DAYS.map((d) => (
              <span key={d} className={styles['dow-cell']}>{d}</span>
            ))}
          </div>

          {/* Day cells */}
          <div className={styles['day-grid']}>
            {cells.map((day, i) => {
              if (!day) return <span key={i} />;
              const ymd = toYMD(new Date(viewYear, viewMonth, day));
              const isSelected = ymd === value;
              const isToday = ymd === todayYMD;
              return (
                <motion.button
                  key={i}
                  type="button"
                  onClick={() => selectDay(day)}
                  className={[
                    styles['day-btn'],
                    isSelected ? styles['day-selected'] : '',
                    isToday && !isSelected ? styles['day-today'] : '',
                  ].filter(Boolean).join(' ')}
                  whileHover={{ scale: isSelected ? 1 : 1.12 }}
                  whileTap={{ scale: 0.88 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 22 }}
                >
                  {day}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className={styles.root}>
      {label && <span className={styles.label}>{label}</span>}

      {/* Trigger */}
      <motion.button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openPicker())}
        className={[styles.trigger, open ? styles['trigger-open'] : ''].join(' ')}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
      >
        <CalendarDays className={styles['trigger-icon']} />
        <span className={value ? styles['trigger-value'] : styles['trigger-placeholder']}>
          {value ? formatDisplay(value) : placeholder}
        </span>
        <AnimatePresence>
          {value && (
            <motion.span
              className={styles['clear-btn']}
              onClick={clear}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.12 }}
            >
              <X style={{ width: 12, height: 12 }} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Portaled calendar — rendered in document.body to escape any parent
          backdrop-filter / overflow / stacking context that would swallow the blur */}
      {createPortal(calendar, document.body)}
    </div>
  );
}
